function plain(value, max = 140) {
  const text = String(value || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trim()}…`;
}

function photographAlt(name, image, extras) {
  const galleryName = plain(name, 80);
  const saved = plain(image && (image.caption || image.alt), 140);
  if (saved && saved.toLowerCase() !== galleryName.toLowerCase()) return saved;
  if (extras && extras.hero) {
    const description = plain(extras.description, 140);
    if (description && description.toLowerCase() !== galleryName.toLowerCase()) return description;
  }
  return galleryName || 'Carmen Lorraine Photography';
}

function hasCloudinaryTransform(url) {
  const marker = '/upload/';
  const index = url.indexOf(marker);
  if (index === -1) return false;
  const first = url.slice(index + marker.length).split('/')[0];
  return /(?:^|,)(?:[a-z]{1,3})_/.test(first);
}

function cloudinaryDeliveryUrl(url, width) {
  if (!url || !url.includes('res.cloudinary.com') || !url.includes('/upload/')) return url || '';
  if (hasCloudinaryTransform(url)) return url;
  const marker = '/upload/';
  const index = url.indexOf(marker);
  const safeWidth = Math.max(1, Math.round(Number(width) || 0));
  return `${url.slice(0, index)}${marker}f_auto,q_auto,c_limit,w_${safeWidth}/${url.slice(index + marker.length)}`;
}

function cloudinarySrcSet(url, widths) {
  if (!url || !url.includes('res.cloudinary.com') || hasCloudinaryTransform(url)) return undefined;
  return widths.map((width) => `${cloudinaryDeliveryUrl(url, width)} ${Math.round(width)}w`).join(', ');
}

// Lightbox candidates. c_limit never upscales, and q_auto keeps Cloudinary's
// normal quality. The original file stays on Cloudinary.
const LIGHTBOX_WIDTHS = [960, 1400, 2000, 2560];

function cloudinaryLightboxWidth(cssWidth, devicePixelRatio) {
  const css = Number(cssWidth);
  const ratio = Number(devicePixelRatio);
  const viewport = Number.isFinite(css) && css > 0 ? css : LIGHTBOX_WIDTHS[0];
  const pixels = Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
  const needed = Math.max(1, Math.round(viewport * pixels));
  return LIGHTBOX_WIDTHS.find((width) => width >= needed) || LIGHTBOX_WIDTHS[LIGHTBOX_WIDTHS.length - 1];
}

function cloudinaryLightbox(url) {
  return {
    src: cloudinaryDeliveryUrl(url, LIGHTBOX_WIDTHS[LIGHTBOX_WIDTHS.length - 1]),
    srcSet: cloudinarySrcSet(url, LIGHTBOX_WIDTHS) || '',
    sizes: '100vw',
  };
}

function cloudinaryPublicPath(uploadPath) {
  let remainder = String(uploadPath || '').replace(/^\//, '');
  while (remainder) {
    const slash = remainder.indexOf('/');
    const first = slash === -1 ? remainder : remainder.slice(0, slash);
    if (/^v\d+$/.test(first)) break;
    if (first.includes(',') || /^(?:w_|h_|c_|q_|f_|b_|g_|ar_|e_|fl_|dpr_)/.test(first)) {
      remainder = slash === -1 ? '' : remainder.slice(slash + 1);
      continue;
    }
    break;
  }
  return remainder;
}

function cloudinaryBase(url) {
  const safe = String(url || '');
  const splitAt = safe.indexOf('/upload/');
  if (!safe.includes('res.cloudinary.com') || splitAt < 0) return null;
  const publicPath = cloudinaryPublicPath(safe.slice(splitAt + '/upload/'.length));
  if (!publicPath) return null;
  return { origin: safe.slice(0, splitAt), publicPath };
}

function withTransform(url, transform) {
  const base = cloudinaryBase(url);
  if (!base) return String(url || '');
  return `${base.origin}/upload/${transform}/${base.publicPath}`;
}

function cloudinaryShareUrl(url) {
  if (!url) return '';
  return withTransform(url, 'w_1200,h_630,c_fill,g_auto,q_auto,f_auto');
}

// Blog photographs. q_auto:best avoids the grain q_auto leaves in skin and sky,
// and withTransform drops any size already baked into the URL.
function cloudinaryBestLimit(url, width) {
  const safeWidth = Math.max(1, Math.round(Number(width) || 0));
  return withTransform(url, `f_auto,q_auto:best,c_limit,w_${safeWidth}`);
}

function cloudinaryBestSrcSet(url, widths) {
  if (!cloudinaryBase(url)) return '';
  return (Array.isArray(widths) ? widths : []).map((width) => {
    const safeWidth = Math.max(1, Math.round(Number(width) || 0));
    return `${cloudinaryBestLimit(url, safeWidth)} ${safeWidth}w`;
  }).join(', ');
}

// Portrait crop for a fixed slot. c_limit leaves a wide frame that the browser
// then enlarges to cover the box, which is what makes these photographs look grainy.
function cloudinaryCover(url, width, height) {
  const safeWidth = Math.max(1, Math.round(Number(width) || 1));
  const safeHeight = Math.max(1, Math.round(Number(height) || 1));
  if (!cloudinaryBase(url)) {
    return { src: String(url || ''), srcSet: '', width: safeWidth, height: safeHeight };
  }
  const build = (targetWidth) => {
    const targetHeight = Math.max(1, Math.round(safeHeight * (targetWidth / safeWidth)));
    return withTransform(url, `c_fill,g_center,f_auto,q_auto:best,w_${targetWidth},h_${targetHeight}`);
  };
  const widths = [480, 720, 960, 1200, 1600].filter((candidate) => candidate < safeWidth).concat(safeWidth);
  return {
    src: build(safeWidth),
    srcSet: widths.map((targetWidth) => `${build(targetWidth)} ${targetWidth}w`).join(', '),
    width: safeWidth,
    height: safeHeight,
  };
}

// Homepage journal photographs sit in a 3:2 frame. Two and four stories share
// a column; three, five, and six use the narrower three-column frame.
function homeStorySlot(count) {
  const total = Number(count) || 1;
  if (total <= 1) {
    return {
      width: 2400,
      height: 1600,
      sizes: '(max-width: 800px) calc(100vw - 2rem), min(75rem, calc((100vw - 4rem) * 0.67))',
    };
  }
  if (total === 2 || total === 4) {
    return {
      width: 2400,
      height: 1600,
      sizes: '(max-width: 900px) calc(100vw - 2rem), min(56rem, calc((100vw - 4rem) / 2))',
    };
  }
  return {
    width: 1800,
    height: 1200,
    sizes: '(max-width: 900px) calc(100vw - 2rem), min(40rem, calc((100vw - 5rem) / 3))',
  };
}

// A cover request larger than the file makes Cloudinary enlarge it, which puts
// the grain back. Shrink the slot so the crop stays inside the original.
function fitCover(slotWidth, slotHeight, source) {
  const sw = Number(source && source.width);
  const sh = Number(source && source.height);
  if (!(sw > 0) || !(sh > 0)) return { width: slotWidth, height: slotHeight };
  const aspect = slotWidth / slotHeight;
  if (sw / sh > aspect) {
    const height = Math.min(slotHeight, Math.floor(sh));
    return { width: Math.max(1, Math.floor(height * aspect)), height };
  }
  const width = Math.min(slotWidth, Math.floor(sw));
  return { width, height: Math.max(1, Math.floor(width / aspect)) };
}

function framedDelivery(url, width, height, sizes, source) {
  const fitted = fitCover(width, height, source);
  const cover = cloudinaryCover(url, fitted.width, fitted.height);
  return {
    src: cover.src || String(url || ''),
    srcSet: cover.srcSet || '',
    sizes,
    width: cover.width,
    height: cover.height,
  };
}

function homeStoryDelivery(url, count, source) {
  const slot = homeStorySlot(count);
  return framedDelivery(url, slot.width, slot.height, slot.sizes, source);
}

// Compact story rows are a 220px 3:2 frame, full width once the row stacks.
function journalRowDelivery(url, source) {
  return framedDelivery(url, 960, 640, '(max-width: 800px) calc(100vw - 2rem), 220px', source);
}

// Blog index cards are a 4:5 frame, up to 420px on a phone and 320px on a desktop.
function journalListDelivery(url, source) {
  return framedDelivery(
    url,
    1280,
    1600,
    '(max-width: 720px) min(26.25rem, calc(100vw - 1.5rem)), 20rem',
    source
  );
}

function cloudinaryStripUrl(url, width, height) {
  const safeWidth = Math.max(1, Math.round(Number(width) || 1200));
  const safeHeight = Math.max(1, Math.round(Number(height) || 800));
  const build = (targetWidth, targetHeight) => withTransform(
    url,
    `c_fill,g_auto,f_auto,q_auto,w_${targetWidth},h_${targetHeight}`
  );
  const src = build(safeWidth, safeHeight);
  const srcset = [400, 800, 1200].map((targetWidth) => {
    const targetHeight = Math.max(1, Math.round(safeHeight * (targetWidth / safeWidth)));
    return `${build(targetWidth, targetHeight)} ${targetWidth}w`;
  }).join(', ');
  return { src, srcset, width: safeWidth, height: safeHeight };
}

module.exports = {
  photographAlt,
  cloudinaryDeliveryUrl,
  cloudinarySrcSet,
  cloudinaryLightbox,
  cloudinaryLightboxWidth,
  cloudinaryPublicPath,
  cloudinaryShareUrl,
  cloudinaryBestLimit,
  cloudinaryBestSrcSet,
  cloudinaryCover,
  homeStorySlot,
  homeStoryDelivery,
  journalRowDelivery,
  journalListDelivery,
  cloudinaryStripUrl,
};
